import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Dropdown, DropdownItem, DropdownMenu, DropdownToggle, NavItem } from "reactstrap";
import { Link } from "react-router-dom";
import useUserAuthentication from "../../_hooks/useUserAuthentication";
import { AuthDataIdentifier } from "../../_statics/AuthDataType";
import {
    hasClientConnectedAuthData,
    mapClientsToConnectedAuthData,
} from "../../organization/client/services/client.mapper";
import { sortNumValuesByField } from "../../_services/sorting-helper";
import styled from "styled-components";
import { useLocation } from "react-router";
import { OrganizationType } from "../../_statics/OrganizationType";

const UserDropdownMenu = () => {
    const clientReducer = useSelector((state) => state.client);
    const user = useSelector((state) => state.authentication.user);
    const { isSuperUser, isInAgency, selectedOrganization } = useUserAuthentication();
    const location = useLocation();

    const [isOpen, setIsOpen] = useState(false);

    const authTypesSet = useMemo(
        () =>
            new Set(
                mapClientsToConnectedAuthData(clientReducer.clients).map((connectAuthData) => connectAuthData.type)
            ),
        [clientReducer]
    );

    const hasChargebee = useMemo(
        () => clientReducer.clients.some((client) => hasClientConnectedAuthData(client, AuthDataIdentifier.CHARGEBEE)),
        [clientReducer]
    );

    const hasStripeConnect = useMemo(
        () =>
            clientReducer.clients.some(
                (client) =>
                    hasClientConnectedAuthData(client, AuthDataIdentifier.STRIPE_CONNECT) ||
                    hasClientConnectedAuthData(client, AuthDataIdentifier.STRIPE_API_KEY)
            ),
        [clientReducer]
    );

    const clientsButtonText = useMemo(() => {
        let text = "";
        if (
            [...authTypesSet].every((type) =>
                [AuthDataIdentifier.STRIPE_CONNECT, AuthDataIdentifier.CHARGEBEE].includes(type)
            ) ||
            [...authTypesSet].every((type) =>
                [AuthDataIdentifier.STRIPE_API_KEY, AuthDataIdentifier.CHARGEBEE].includes(type)
            )
        ) {
            text = "Accounts";
        } else if (!hasStripeConnect && !hasChargebee) {
            text = "Shops";
        } else {
            text = "Shops & Accounts";
        }

        return text;
    }, [authTypesSet, hasStripeConnect, hasChargebee]);

    const availableUserDropdownItems = useMemo(() => {
        let items = [
            {
                name: "Profil",
                url: "/user",
                dataCy: "user-profile-button",
                icon: "fas fa-user",
                index: 0,
            },
            {
                name: "Administration",
                url: "/adminarea",
                dataCy: "user-admin-button",
                icon: "fas fa-user",
                hidden: !(isSuperUser || isInAgency),
                index: 1,
            },
            {
                name: "Mein Unternehmen",
                url: "/organization",
                dataCy: "user-company-button",
                icon: "fas fa-user",
                index: 3,
            },

            {
                name: "Logout",
                url: "/dologout",
                dataCy: "logout-button",
                icon: "fas fa-lock",
                index: 5,
            },
        ];

        if (selectedOrganization) {
            items.push({
                name: "Administration (" + selectedOrganization.name + ")",
                url: "/adminarea/organization/" + selectedOrganization.externalUUID,
                dataCy: "user-admin-button",
                icon: "fas fa-user",
                hidden: !(isSuperUser || isInAgency),
                index: 2,
            });
        }

        if (selectedOrganization && selectedOrganization.organizationType !== OrganizationType.TAX_AGENCY) {
            items.push({
                name:
                    clientsButtonText +
                    (clientReducer && clientReducer.clients ? " (" + clientReducer.clients.length + ")" : ""),
                url: "/clients",
                dataCy: "user-clients-button",
                icon: "fas fa-user",
                index: 4,
            });
        }
        return items;
    }, [clientReducer, clientsButtonText, isInAgency, isSuperUser, selectedOrganization]);

    useEffect(() => {
        setIsOpen(false);
    }, [location.pathname]);

    if (!user) {
        return <></>;
    } else {
        return (
            <Dropdown direction="down" isOpen={isOpen} toggle={() => setIsOpen(!isOpen)}>
                <DropdownToggle
                    nav
                    data-cy="user-name-toggle"
                    style={{ paddingBottom: "0" }}
                    onClick={() => setIsOpen(!isOpen)}
                >
                    <h6 style={{ marginBottom: "0" }}>
                        {user.firstName} {user.lastName} <i className="fas fa-caret-down" />
                    </h6>
                </DropdownToggle>
                <DropdownMenu
                    end
                    style={{
                        width: "100%",
                        maxHeight: "500px",
                        minWidth: "300px",
                        overflow: "auto",
                        transform: "translate3d(-531px, 16px, 0px) !important",
                        right: "auto !important",
                    }}
                    aria-expanded={true}
                >
                    <DropdownItem header tag="div" className="text-center bg-dark-subtle">
                        <strong>Benutzer</strong>
                    </DropdownItem>
                    {sortNumValuesByField(availableUserDropdownItems, "index")
                        .filter((item) => !item.hidden)
                        .map((item, index) => {
                            return (
                                <CustomNavItem data-cy={item.dataCy} key={index}>
                                    <Link to={item.url}>
                                        <i className={item.icon} />
                                        {item.name}
                                    </Link>
                                </CustomNavItem>
                            );
                        })}
                </DropdownMenu>
            </Dropdown>
        );
    }
};

export default UserDropdownMenu;

const CustomNavItem = styled(NavItem)`
    display: grid;
    color: var(--light) !important;
    padding: 0;
    align-items: center;
    border-top: 1px solid #c8ced3;

    &:hover {
        background: var(--light) !important;
    }

    & a {
        display: grid;
        grid-template-columns: 20px auto;
        column-gap: 0.5rem;
        color: #23282c !important;
        padding: 0.75rem 1rem;
        text-decoration: none;
        font-weight: 400;
    }

    & i {
        display: inline-block;
        width: 1.09375rem;
        margin: 0 0.5rem 0 0;
        font-size: 0.875rem;
        color: #23282c;
        text-align: center;
        align-self: center !important;
        line-height: unset !important;
    }
`;
